import {Circle} from "@react-google-maps/api";
import {useNavigate} from "react-router-dom";
import {
  BaseEntitySelect,
  SiteConnectionLines,
  SiteMarkerBadge,
  fitMapToPoints,
  InfoWindowDetail,
  isContextMapDetail,
  isEntityMapDetail,
  LocationContextInfoPanel,
  LocationContextInfoWindow,
  LocationContextMarker,
  Map as MapComponent,
  MapDetail,
  MapFilter,
  MapState,
  QuickPanelContainer,
  ResizeableContent,
  routingColorByIndex,
  RoutingPolyline,
  SiteInfoPanelMemo,
  SiteInfoWindow,
  SiteMarkerClustererMemo,
  TopPanel,
  TrafficLayer,
  useApp,
  useGetContainerStatuses,
  useSiteConnections,
  useFleetMapData,
  useMapLoader,
} from "@ots-frontend/ots-ui";
import {useCallback, useEffect, useMemo, useState} from "react";
import {
  Entity,
  firstOrUndefined,
  isSite,
  LatLng,
  LocatableEntity,
  LocationContext,
  RouteResult,
  SiteEntity,
  useFleetTracking,
  useLazyGetRoutingOriginManyResultQuery
} from "@ots-frontend/ots-data";
import {
  DEFAULT_LIVE_MAP_DISPLAY,
  isLiveMapPositionStale,
  unitLoadRing,
  useMockFleetLocations,
  liveMapTypeId,
  showLiveMapGeofences,
  showLiveMapLabels,
  siteLoadBadges,
  useLiveMapDisplay,
  useLiveMapNow,
} from "@ots-frontend/ots-ui-tw";
import {useTranslation} from "react-i18next";
import {notifications} from "@mantine/notifications";
import {getMapFilter, getMapFocusPoint, getMapState} from "../../store/map/selectors";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {setMapFilterEntity, setMapFocusPoint, setMapState} from "../../store/map/actions";
import {useDispatch} from "react-redux";
import {MapFocusPoint} from "../../model/map";
import styled from "@emotion/styled";
import {FaSearch} from "react-icons/all";

const SearchPanelContainer = styled.div`
  pointer-events: auto;
  border: 0;
  padding: 10px;
  position: absolute;
  top: 0;
  left: 0;
`;

const DEFAULT_MAP_OPTIONS: google.maps.MapOptions = {
  mapTypeControl: false,
  streetViewControl: false
};

// Units draw over the sites.
const UNIT_MARKER_OPTIONS: google.maps.MarkerOptions = {zIndex: 10_000};

// Dispatchers pick units out by name, so DispatchGo labels them unless turned off.
const DISPATCH_GO_MAP_DISPLAY = {...DEFAULT_LIVE_MAP_DISPLAY, labels: true};

const DEFAULT_CENTER: google.maps.LatLngLiteral = {
  lat: 38.685,
  lng: -115.234
};

const DEFAULT_BOUNDS: google.maps.LatLngBoundsLiteral = {
  south: 18.853694845822282,
  west: -139.12288925000001,
  north: 49.75245970922224,
  east: -63.53695175000002
};

export const LiveMap = () => {
    const {regionId} = useApp();
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {display: mapDisplay, onDisplayChange: onMapDisplayChange} = useLiveMapDisplay({defaults: DISPATCH_GO_MAP_DISPLAY});
    const now = useLiveMapNow();
    // The type goes in with the options: set apart from them, the first load keeps the default road map.
    const mapTypeId = liveMapTypeId(mapDisplay);
    const mapOptions = useMemo<google.maps.MapOptions>(() => ({...DEFAULT_MAP_OPTIONS, mapTypeId}), [mapTypeId]);

    const mapFilter = useAppSelector<MapFilter>(getMapFilter);
    const mapState = useAppSelector<MapState | undefined>(getMapState);
    const focusPoint = useAppSelector<MapFocusPoint | undefined>(getMapFocusPoint);

    // map state
    const [map, setMap] = useState<google.maps.Map | undefined>();
    const [mapZoom, setMapZoom] = useState<number>(focusPoint?.latLng ? 15 : 8);
    const [mapCenter, _setMapCenter] = useState<google.maps.LatLngLiteral>({
      lat: focusPoint?.latLng?.latCoord ?? DEFAULT_CENTER.lat,
      lng: focusPoint?.latLng?.longCoord ?? DEFAULT_CENTER.lng
    });
    const [geofenceSites, setGeofenceSites] = useState<SiteEntity[]>([]);
    const [zoom, setZoom] = useState<number | undefined>();
    const labelsShown = showLiveMapLabels(mapDisplay, zoom);

    // The region's container statuses, the same cached request the site panel and Container Status page use.
    const {containerStatuses} = useGetContainerStatuses();
    const loadBadges = useMemo(() => siteLoadBadges(containerStatuses), [containerStatuses]);
    const siteBadge = useMemo(() => {
      if (!mapDisplay.siteLoads) return undefined;
      return (site: SiteEntity): SiteMarkerBadge | undefined => {
        const badge = loadBadges.get(site.id);
        return badge && {count: badge.availableLoads, alert: badge.critical};
      };
    }, [loadBadges, mapDisplay.siteLoads]);

    // popup state
    const [popupWindow, setPopupWindow] = useState<InfoWindowDetail | undefined>(undefined);
    const [hoverPopupWindow, setHoverPopupWindow] = useState<InfoWindowDetail | undefined>(undefined);

    // route state
    const [selectedRoutes, setSelectedRoutes] = useState<RouteResult[]>([]);
    const [triggerGetRoutes] = useLazyGetRoutingOriginManyResultQuery();

    const {isLoaded: isMapLoaded} = useMapLoader({
      googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY
    });

    const {
      locations: trackedLocations,
      isLoading,
      isFetching,
      error
    } = useFleetTracking({
      regionId: regionId,
      type: mapFilter.entity,
    })

    const {
      data,
      isLoading: isDataLoading,
      isFetching: isDataFetching,
    } = useFleetMapData({
      regionId: regionId,
      mapFilter: mapFilter,
    })

    const sites = data.sites
    // Dev only: ?mockFleet in the URL adds made-up units around the sites, in every load state; see mock-fleet.
    const locations = useMockFleetLocations(trackedLocations, {enabled: import.meta.env.DEV, entityType: mapFilter.entity, sites, now});

    const locationContextIndex = useMemo(() => {
      const map = new Map<string, LocationContext>();
      for (const ctx of locations) {
        if (!ctx?.entityId || !ctx?.entityType) {
          continue
        }

        map.set(`${ctx.entityType}::${ctx.entityId}`, ctx);
      }
      return map;
    }, [locations]);

    const findLocation = useCallback((entity: Entity) => {
      const key = `${entity.entityType}::${entity.id}`
      return locationContextIndex.get(key);
    }, [locationContextIndex])

    const _goToLocation = useCallback(
      (map: google.maps.Map | undefined, focusPoint: MapFocusPoint | undefined) => {
        if (map) {
          const location = focusPoint?.latLng;
          if (location) {
            map?.panTo({lat: location.latCoord, lng: location.longCoord});
            map?.setZoom(15);
          } else {
            notifications.show({
              color: "yellow",
              message: t<string>("page.map.focuspoint.error.message")
            });
          }
        }
      },
      [t]
    );

    const goToLocation = useCallback(
      (focusPoint: MapFocusPoint | undefined) => {
        _goToLocation(map, focusPoint);
      },
      [map, _goToLocation]
    );

    useEffect(() => {
      if (map && focusPoint) {
        goToLocation(focusPoint);
        dispatch(setMapFocusPoint(undefined));
      }
    }, [map, goToLocation, focusPoint, dispatch]);

    const onEntitySearchSelect = useCallback((entities: Entity[]) => {
      const entity = firstOrUndefined(entities);
      if (entity) {
        if (isSite(entity)) {
          goToLocation(entity)
          setPopupWindow({
            detail: {
              mode: "entity",
              context: entity,
            },
            latLng: new google.maps.LatLng({lat: entity.latLng!.latCoord!, lng: entity.latLng!.longCoord!})
          });
        } else {
          const context = findLocation(entity)
          if (context && context.location) {
            goToLocation({
              id: context.entityId,
              displayName: context.entityName,
              latLng: {
                latCoord: context.location.latitude,
                longCoord: context.location.longitude,
              }
            })
            setPopupWindow({
              detail: {
                mode: "context",
                context: context,
              },
              latLng: new google.maps.LatLng({lat: context.location.latitude, lng: context.location.longitude})
            });
          } else {
            goToLocation(undefined)
          }
        }
      }
    }, [findLocation, goToLocation])

    const onMapLoad = useCallback((map: google.maps.Map) => {
      setMap(map);

      if (focusPoint) {
        _goToLocation(map, focusPoint);
      } else if (mapState?.bounds) {
        map.fitBounds(mapState.bounds);
      } else {
        map.fitBounds(DEFAULT_BOUNDS);
      }
    }, [focusPoint, mapState?.bounds, _goToLocation]);

    const onMapUnmount = useCallback((map: google.maps.Map) => {
      dispatch(setMapState({bounds: map.getBounds()?.toJSON()}));
      dispatch(setMapFocusPoint(undefined));
      setMap(undefined);
    }, [dispatch]);


    const onFitFleet = useCallback(() => {
      const points = locations.flatMap(({location}) => location ? [{lat: location.latitude, lng: location.longitude}] : []);
      if (map && !fitMapToPoints(map, points)) {
        notifications.show({color: "yellow", message: "No tracked units to show."});
      }
    }, [map, locations]);

    const onFitSites = useCallback(() => {
      const points = sites.flatMap(({location}) => location ? [{lat: location.latCoord, lng: location.longCoord}] : []);
      if (map && !fitMapToPoints(map, points)) {
        notifications.show({color: "yellow", message: "No sites to show."});
      }
    }, [map, sites]);

    const onMapZoomChanged = useCallback(() => {
      // TODO: should we memoize the geofence sites?
      const zoom = map?.getZoom();
      setZoom(zoom);
      if (zoom) {
        if (!showLiveMapGeofences(mapDisplay, zoom)) {
          setGeofenceSites([]);
        } else {
          const newGeofenceSites: SiteEntity[] = [];
          const bounds = map?.getBounds();
          if (bounds) {
            for (const site of sites) {
              if (site.location && site.geofenceExtension) {
                const latLng: google.maps.LatLngLiteral = {
                  lat: site.location.latCoord,
                  lng: site.location.longCoord
                };
                if (bounds.contains(latLng)) {
                  newGeofenceSites.push(site);
                }
              }
            }
          }
          setGeofenceSites(newGeofenceSites);
        }
      }
    }, [map, sites, mapDisplay]);

    const onHoverOut = useCallback((_event: google.maps.MapMouseEvent) => {
      setHoverPopupWindow(undefined);
    }, []);

    const onHover = useCallback((event: google.maps.MapMouseEvent, detail: MapDetail) => {
        if (event.latLng) {
          setHoverPopupWindow({detail: detail, latLng: event.latLng});
        } else {
          setHoverPopupWindow(undefined);
        }
      }, []
    );

    const onEntityHover = useCallback((event: google.maps.MapMouseEvent, context: LocatableEntity) => {
        onHover(event, {mode: 'entity', context: context});
      }, [onHover]
    );

    const onContextHover = useCallback((event: google.maps.MapMouseEvent, context: LocationContext) => {
        onHover(event, {mode: 'context', context: context});
      }, [onHover]
    );

    const onMarkerClick = useCallback(
      (event: google.maps.MapMouseEvent, detail: MapDetail) => {
        if (event.latLng) {
          setPopupWindow({detail: detail, latLng: event.latLng});
        } else {
          setPopupWindow(undefined);
        }
        onHover(event, detail);
      },
      [onHover]
    );

    const onEntityMarkerClick = useCallback(
      (event: google.maps.MapMouseEvent, context: LocatableEntity) => {
        onMarkerClick(event, {mode: 'entity', context: context});
      },
      [onMarkerClick]
    );

    const onContextMarkerClick = useCallback(
      (event: google.maps.MapMouseEvent, context: LocationContext) => {
        onMarkerClick(event, {mode: 'context', context: context});
      },
      [onMarkerClick]
    );

    const clearPopup = useCallback(() => {
      setPopupWindow(undefined)
    }, [])

    useEffect(() => {
      const fetch = async () => {

        if (!popupWindow || !isContextMapDetail(popupWindow.detail)) {
          setSelectedRoutes([])
          return
        }

        const context = popupWindow.detail.context;

        let origin: LatLng | undefined = undefined
        const destinations: LatLng[] = []

        if (context.location) {
          origin = {lat: context.location.latitude, lon: context.location.longitude}
        }

        const possibleDests = Object.values(context.meta?.possibleDestinations || {});
        for (const possibleDest of possibleDests) {
          if (possibleDest.latitude && possibleDest.longitude) {
            destinations.push({lat: possibleDest.latitude, lon: possibleDest.longitude})
          }
        }

        if (!origin || !destinations.length) {
          setSelectedRoutes([])
          return null
        }

        await triggerGetRoutes({
          origin: origin,
          destinations: destinations
        })
          .unwrap()
          .then((response) => {
            const routes = response.results
              .map(r => r.route)
              .filter((value): value is RouteResult => value !== undefined);
            setSelectedRoutes(routes)
          })
          .catch((rejected) =>
            setSelectedRoutes([])
          );
      }

      fetch();
    }, [popupWindow, triggerGetRoutes]);

    const renderEntityPopup = useCallback((window: InfoWindowDetail | undefined) => {
        if (window) {
          if (isEntityMapDetail(window.detail)) {
            if (isSite(window.detail.context)) {
              return (
                <SiteInfoWindow
                  entity={window.detail.context}
                  position={window.latLng}
                  onCloseClick={() => setPopupWindow(undefined)}
                />
              );
            }
          } else if (isContextMapDetail(window.detail)) {
            return (
              <LocationContextInfoWindow
                ctx={window.detail.context}
                position={window.latLng}
                onCloseClick={() => setPopupWindow(undefined)}
              />
            );
          }
        }

        return undefined
      },
      []
    );

    const renderHoverEntityPopup = useCallback(() => {
      return renderEntityPopup(hoverPopupWindow);
    }, [renderEntityPopup, hoverPopupWindow]);

    const renderMarkerEntityPopup = useCallback(() => {
      return renderEntityPopup(popupWindow);
    }, [renderEntityPopup, popupWindow]);

    useEffect(() => {
      onMapZoomChanged();
    }, [onMapZoomChanged, map, sites]);

    const onEntityViewChange = useCallback((entity: string) => {
      if (entity === "VEHICLE" || entity === "TRAILER" || entity === "PERSON") {
        dispatch(setMapFilterEntity(entity));
      }
    }, [dispatch])

    // A clicked site draws lines to nearby units (or those bound for it), and lists them in its panel.
    const selectedSite = popupWindow && isEntityMapDetail(popupWindow.detail) && isSite(popupWindow.detail.context)
      ? popupWindow.detail.context as SiteEntity
      : undefined;
    const {connections: siteConnections, roadRoutes: siteRoadRoutes} = useSiteConnections(selectedSite, locations, mapDisplay);

    const navigate = useNavigate();
    const onOpenTicket = useCallback((tta: {id: string; database?: string}) =>
      navigate(`/tickets/profile?id=${tta.id}${tta.database ? `&database=${tta.database}` : ""}`), [navigate]);

    const onSelectNearbyUnit = useCallback((ctx: LocationContext) => {
      if (!ctx.location) return;
      const latLng = {lat: ctx.location.latitude, lng: ctx.location.longitude};
      map?.panTo(latLng);
      setPopupWindow({detail: {mode: "context", context: ctx}, latLng: new google.maps.LatLng(latLng)});
    }, [map]);

    const siteNearby = useMemo(() => {
      if (!selectedSite || mapDisplay.siteLinks === "off") return undefined;
      return {mode: mapDisplay.siteLinks, connections: siteConnections, roadRoutes: siteRoadRoutes, onSelect: onSelectNearbyUnit};
    }, [selectedSite, mapDisplay.siteLinks, siteConnections, siteRoadRoutes, onSelectNearbyUnit]);

    if (!isMapLoaded) {
      return <div/>;
    }

    return (
      <ResizeableContent>
        {(size) => (
          <MapComponent
            onLoad={onMapLoad}
            onUnmount={onMapUnmount}
            onZoomChanged={onMapZoomChanged}
            onBoundsChanged={onMapZoomChanged}
            zoom={mapZoom}
            center={mapCenter}
            mapContainerStyle={{width: size.width ?? "100%", height: size.height ?? "100%"}}
            options={mapOptions}
          >

            {mapDisplay.traffic && <TrafficLayer/>}

            <SiteMarkerClustererMemo
              entities={sites}
              badge={siteBadge}
              onHover={onEntityHover}
              onHoverOut={onHoverOut}
              onMarkerClick={onEntityMarkerClick}/>

            {locations && (
              <>
                {locations.map((loc => (
                  <LocationContextMarker
                    key={loc.entityId}
                    ctx={loc}
                    stale={isLiveMapPositionStale(loc.location?.dttm, mapDisplay, now)}
                    label={labelsShown ? loc.entityName : undefined}
                    loadRing={mapDisplay.unitLoads ? unitLoadRing(loc) : undefined}
                    onHover={onContextHover}
                    onHoverOut={onHoverOut}
                    onMarkerClick={onContextMarkerClick}
                    options={UNIT_MARKER_OPTIONS}
                  />
                )))}
              </>
            )}

            {geofenceSites.map((s) => {
              return (
                <Circle
                  key={s.id + "-geofence"}
                  center={{lat: s.latCoord!, lng: s.longCoord!}}
                  radius={s.geofenceExtension!}
                />
              );
            })}

            {selectedRoutes.map((r, i) => (
              <RoutingPolyline
                id={`routing-${i}`}
                route={r}
                geodesic={true}
                strokeColor={routingColorByIndex(i)}
              />
            ))}

            {selectedSite && (
              <SiteConnectionLines site={selectedSite} display={mapDisplay} connections={siteConnections} roadRoutes={siteRoadRoutes}/>
            )}

            {renderHoverEntityPopup()}
            {renderMarkerEntityPopup()}

            <TopPanel
              entity={mapFilter.entity}
              onEntityChange={onEntityViewChange}
              display={mapDisplay}
              onDisplayChange={onMapDisplayChange}
              displayDefaults={DISPATCH_GO_MAP_DISPLAY}
              onFitFleet={onFitFleet}
              onFitSites={onFitSites}
            />

            <SearchPanelContainer>
              <BaseEntitySelect
                loading={isDataLoading}
                icon={<FaSearch/>}
                w={375}
                limit={5}
                entities={[...sites, ...data.trailers, ...data.vehicles, ...data.persons]}
                onEntityChange={onEntitySearchSelect}
                placeholder={"Search"}
                clearable={true}/>
            </SearchPanelContainer>

            {popupWindow &&
              <>
                {(isEntityMapDetail(popupWindow.detail) && isSite(popupWindow.detail.context)) && (
                  <QuickPanelContainer onCloseClick={clearPopup}>
                    <SiteInfoPanelMemo site={popupWindow.detail.context} nearby={siteNearby} ticketParty="driver" onOpenTicket={onOpenTicket}/>
                  </QuickPanelContainer>
                )
                }
                {
                  isContextMapDetail(popupWindow.detail) &&
                  <QuickPanelContainer onCloseClick={clearPopup}>
                    <LocationContextInfoPanel ctx={popupWindow.detail.context}/>
                  </QuickPanelContainer>
                }
              </>
            }
          </MapComponent>
        )
        }
      </ResizeableContent>
    )
      ;
  }
;
